/**
 * Types for the public customer tracking endpoint (Delivery v2, ENG-5514).
 *
 *   GET {orderApiBackend}/backend/v2/delivery/track/{order_hash}/
 *   MQTT /delivery/{order_hash}/track   (same shape, throttled to one per 15 s)
 *
 * Only the fields a customer is allowed to see are modelled here. The driver is
 * deliberately limited to a first name and an initial.
 */

export type DeliveryState =
  | 'PENDING'
  | 'ASSIGNED'
  | 'EN_ROUTE'
  | 'ARRIVED'
  | 'DELIVERED'
  | 'FAILED'
  | 'CANCELLED';

export const DELIVERY_STATES: ReadonlyArray<DeliveryState> = [
  'PENDING',
  'ASSIGNED',
  'EN_ROUTE',
  'ARRIVED',
  'DELIVERED',
  'FAILED',
  'CANCELLED',
];

/** States after which nothing changes any more, so polling stops. */
export const TERMINAL_DELIVERY_STATES: ReadonlyArray<DeliveryState> = [
  'DELIVERED',
  'FAILED',
  'CANCELLED',
];

export interface LatLng {
  lat: number;
  lng: number;
}

export interface DeliveryTrackStore extends Partial<LatLng> {
  phone?: string | null;
}

export interface DeliveryTrackDriver {
  first_name: string;
  initial?: string | null;
}

export interface DeliveryTrackTimes {
  placed_at?: string | null;
  ready_at?: string | null;
  dispatched_at?: string | null;
  arrived_at?: string | null;
  delivered_at?: string | null;
}

export interface DeliveryTrackPosition extends LatLng {
  /** Degrees clockwise from north. */
  heading?: number | null;
  recorded_at?: string | null;
}

export interface DeliveryTrackPod {
  type?: string | null;
  photo_url?: string | null;
  note?: string | null;
}

export interface DeliveryTrack {
  state: DeliveryState;
  order_counter?: string | null;
  store_name?: string | null;
  store?: DeliveryTrackStore | null;
  destination?: Partial<LatLng> | null;
  driver?: DeliveryTrackDriver | null;
  times?: DeliveryTrackTimes | null;
  eta_at?: string | null;
  eta_minutes?: number | null;
  live_map?: boolean | null;
  position?: DeliveryTrackPosition | null;
  pod?: DeliveryTrackPod | null;
}

export function isDeliveryState(value: unknown): value is DeliveryState {
  return typeof value === 'string' && (DELIVERY_STATES as ReadonlyArray<string>).includes(value);
}

export function isTerminalState(state: DeliveryState): boolean {
  return TERMINAL_DELIVERY_STATES.includes(state);
}

export function isValidLatLng(point: Partial<LatLng> | null | undefined): point is LatLng {
  return (
    !!point &&
    typeof point.lat === 'number' &&
    typeof point.lng === 'number' &&
    isFinite(point.lat) &&
    isFinite(point.lng) &&
    Math.abs(point.lat) <= 90 &&
    Math.abs(point.lng) <= 180
  );
}

/**
 * Copies only the customer-safe fields out of a raw payload. Anything the
 * backend might add later (driver last name, phone, other stops of the run)
 * is dropped here, so it can never reach a template by accident.
 * Returns null when the payload is not a track response at all.
 */
export function sanitizeTrack(raw: any): DeliveryTrack | null {
  if (!raw || typeof raw !== 'object' || !isDeliveryState(raw.state)) {
    return null;
  }
  const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
  const num = (v: unknown): number | null => (typeof v === 'number' && isFinite(v) ? v : null);
  const point = (v: any): LatLng | null => (isValidLatLng(v) ? { lat: v.lat, lng: v.lng } : null);

  const firstName = raw.driver ? str(raw.driver.first_name) : null;
  const times = raw.times && typeof raw.times === 'object' ? raw.times : {};
  const position = point(raw.position);
  const store = point(raw.store);
  const photoUrl = raw.pod ? str(raw.pod.photo_url) : null;

  return {
    state: raw.state,
    order_counter: raw.order_counter != null ? String(raw.order_counter) : null,
    store_name: str(raw.store_name),
    store: raw.store ? { ...(store || {}), phone: str(raw.store.phone) } : null,
    destination: point(raw.destination),
    driver: firstName
      ? {
          // First word only: a full name must never be shown to the customer.
          first_name: firstName.trim().split(/\s+/)[0],
          initial: (str(raw.driver.initial) || firstName.trim()).charAt(0).toUpperCase(),
        }
      : null,
    times: {
      placed_at: str(times.placed_at),
      ready_at: str(times.ready_at),
      dispatched_at: str(times.dispatched_at),
      arrived_at: str(times.arrived_at),
      delivered_at: str(times.delivered_at),
    },
    eta_at: str(raw.eta_at),
    eta_minutes: num(raw.eta_minutes),
    live_map: raw.live_map === true,
    position: position
      ? { ...position, heading: num(raw.position.heading), recorded_at: str(raw.position.recorded_at) }
      : null,
    pod: raw.pod
      ? { type: str(raw.pod.type), photo_url: isSafePhotoUrl(photoUrl) ? photoUrl : null, note: str(raw.pod.note) }
      : null,
  };
}

/** Proof photos must be http(s) or app-relative; never javascript:/data: URLs. */
export function isSafePhotoUrl(url: string | null): boolean {
  if (!url) {
    return false;
  }
  return /^https?:\/\//i.test(url) || /^(\/|\.\/|assets\/)/.test(url);
}
